'use client'

import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'

// Optional navigations (adjust to your actual routes):
const navigation = [
  { name: 'Product', href: '#' },
  { name: 'Features', href: '#' },
  { name: 'Resources', href: '#' },
  { name: 'Company', href: '#' },
]

// Tailor these “stats” to reflect real achievements:
const stats = [
  { label: 'Years of Professional Experience', value: '20+' },
  { label: 'Major Projects Completed', value: '100+' },
  { label: 'Depts Streamlined with Custom ERP', value: '5' },
]

const expertiseList = [
  {
    name: 'System Administration & Cloud',
    description:
      'Over two decades of hands-on experience deploying and maintaining Windows, macOS, and Linux environments, plus major cloud providers like Microsoft Azure and Google Cloud.',
  },
  {
    name: 'Networking & Security',
    description:
      'Proficient in LAN, VLAN, QoS, VPN, firewall management, and monitoring. Committed to designing secure, resilient networks.',
  },
  {
    name: 'Software Development & Automation',
    description:
      'Specialized in LAMP stack, MERN stack, Python scripting, and workflow automation. Known for custom ERP solutions that boost workflow efficiency.',
  },
  {
    name: 'CAD & Fire Alarm Design',
    description:
      'Proficient in creating and modifying system designs with AutoCAD. Experienced with NY building codes, NFPA standards, and high-profile client demands.',
  },
  {
    name: 'Data Backup & Recovery',
    description:
      'Implement robust backup strategies (on-prem & cloud) and disaster recovery solutions. Ensured 100% data protection across multiple orgs.',
  },
  {
    name: 'Team Leadership & Collaboration',
    description:
      'Proven history of guiding cross-functional teams, collaborating with stakeholders, and delivering results on schedule.',
  },
]

// You could repurpose this section for certifications, if desired:
const certifications = [
  {
    name: 'CompTIA A+',
    role: 'Industry-standard validation of technical & support skills',
    imageUrl:'https://blog.angeloruiz.com/wp-content/uploads/2025/10/comptia.webp'
  },
  // Add additional certs or achievements...
]

// You might keep or remove “blogPosts” if you have a blog:
const blogPosts = [
  {
    id: 1,
    title: 'Tips on Scaling Legacy Systems Efficiently',
    href: '#',
    description:
      'Learn how I integrated a custom ERP across multiple departments, boosting productivity while maintaining rock-solid security.',
    imageUrl:
      'https://images.unsplash.com/photo-1496128858413-b36217c2ce36?ixlib=rb-4.0.3&auto=format&fit=crop&w=3603&q=80',
    date: 'Mar 16, 2024',
    datetime: '2024-03-16',
    author: {
      name: 'Angelo Ruiz',
      imageUrl:
        'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    },
  },
  // More posts...
]

const timeline = [
  {
    name: 'IT Consultant (freelance)',
    description:
    'Provided hands-on technical support and strategic IT guidance for small Businesses',
    date: 'Aug 2000',
    dateTime: '2000-08',
  },
  {
    name: 'Senior Cad Designer at Cross-Fire',
    description:'Designed and documented fire alarm systems in AutoCAD for high-profile projects, ensuring code compliance, coordinating with teams, conducting site visits, and developing CAD standards and LISP automation tools to improve design efficiency and accuracy.',
    date: 'Apr 2004',
    dateTime: '2004-04',
  },
  {
    name: 'IT Manager at Cross-Fire',
    description:
    'Implemented and maintained the organization’s IT infrastructure — overseeing network security, O365 migration, and business continuity — while developing automation scripts, full-stack web applications, and database integrations to improve system efficiency, security, and team productivity.',
    date: 'Mar 2010',
    dateTime: '2010-03',
  },
  {
    name: 'System Administrator at IRL-Systems',
    description:
      'Implemented and maintained the organization’s IT infrastructure — overseeing network security. Built automation scripts to improve system efficiency, security, and team productivity.',
    date: 'Mar 2024',
    dateTime: '2024-03',
  },
  {
    name: 'Co-founder at Aileron Systems',
    description:
      'Developed a full-scale web application that unified operations across all departments—covering installations, accounting, sales, maintenance, service, and compliance—automating workflows, tracking projects, payroll, inspections, and billing, while integrating real-time notifications, role-based access, and reporting to significantly improve efficiency, transparency, and productivity company-wide.',
    date: 'Sep 2025',
    dateTime: '2025-09',
  },
]

export default function AboutPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="bg-white">
      <main className="isolate">
        {/* Hero section */}
        <div className="relative isolate -z-10">
          {/* (Decorative SVG background omitted for brevity) */}
          <div className="overflow-hidden">
            <div className="mx-auto max-w-7xl px-6 pb-32 pt-36 sm:pt-60 lg:px-8 lg:pt-32">
              <div className="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center">
                {/* LEFT COLUMN */}
                <div className="relative w-full lg:max-w-xl lg:shrink-0 xl:max-w-2xl">
                  <h1 className="text-pretty text-5xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                    About Me
                  </h1>
                  <p className="mt-8 text-pretty text-lg font-medium text-gray-500 sm:max-w-md sm:text-xl/8 lg:max-w-none">
                    I’m a Senior IT Specialist with over two decades of professional experience in system administration,
                    networking, and software development. I excel at designing secure, scalable solutions and automating
                    workflows to maximize efficiency.
                  </p>
                </div>

                {/* RIGHT COLUMN (images) */}
                <div className="mt-14 flex justify-end gap-8 sm:-mt-44 sm:justify-start sm:pl-20 lg:mt-0 lg:pl-0">
                  {/* IMAGE examples omitted for brevity */}
                  {/* You can remove or replace them with your own images */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content section */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 xl:-mt-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
              My Mission
            </h2>
            <div className="mt-6 flex flex-col gap-x-8 gap-y-20 lg:flex-row">
              <div className="lg:w-full lg:max-w-2xl lg:flex-auto">
                <p className="text-lg/8 text-gray-700">
                  My mission is to bridge the gap between cutting-edge technology and real-world business needs. Whether
                  I’m optimizing server environments, building custom ERP solutions, or leading a cloud migration, I aim
                  to empower teams to work smarter and securely.
                </p>
                <p className="mt-5 max-w-xl text-base/7 text-gray-700">
                  I thrive on delivering robust, cost-effective IT infrastructures that scale effortlessly. By focusing
                  on innovation, automation, and user-friendly solutions, I help organizations streamline their
                  operations and stay ahead in a rapidly evolving tech landscape.
                </p>
              </div>
              <div className="lg:flex lg:flex-auto lg:justify-center">
                <dl className="w-64 space-y-8 xl:w-80">
                  {stats.map((stat) => (
                    <div key={stat.label} className="flex flex-col-reverse gap-y-4">
                      <dt className="text-base/7 text-gray-600">{stat.label}</dt>
                      <dd className="text-5xl font-semibold tracking-tight text-gray-900">{stat.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Optional image break */}
        <div className="mt-32 sm:mt-40 xl:mx-auto xl:max-w-7xl xl:px-8">
          <img
            alt="Hero or personal photo"
            src="https://blog.angeloruiz.com/wp-content/uploads/2025/10/Dark-Blue-Modern-Glow-Software-Solution-LinkedIn-Background-Photo.png"
            className="aspect-[5/2] w-full object-cover xl:rounded-2xl"
          />
        </div>

        {/* Expertise section (previously "Our values") */}
    <div className="bg-white py-10 sm:py-22">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <h2 className="text-pretty text-3xl font-semibold tracking-tight text-gray-900 sm:text-3xl p-4">
              Experience
        </h2>
        <div className="mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-4">
          {timeline.map((item) => (
            <div key={item.name}>
              <time dateTime={item.dateTime} className="flex items-center text-sm/6 font-semibold text-indigo-600">
                <svg viewBox="0 0 4 4" aria-hidden="true" className="mr-4 size-1 flex-none">
                  <circle r={2} cx={2} cy={2} fill="currentColor" />
                </svg>
                {item.date}
                <div
                  aria-hidden="true"
                  className="absolute -ml-2 h-px w-screen -translate-x-full bg-gray-900/10 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
                />
              </time>
              <p className="mt-6 text-lg/8 font-semibold tracking-tight text-gray-900">{item.name}</p>
              <p className="mt-1 text-base/7 text-gray-600">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>        

        {/* Optional “Trusted by” or “Logo cloud” */}
        <div className="relative isolate -z-10 mt-20 sm:mt-20 bg-black/5 py-10">
          {/* Decorative background omitted */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="text-center text-lg/8 font-semibold text-gray-900">
              Trusted by forward-thinking organizations
            </h2>
            {/* Place any partner or client logos here, if desired */}
          </div>
        </div>

        {/* “Certifications & Achievements” Section (replacing “Our team”) */}
        <div className="mx-auto mt-20 max-w-7xl px-6 sm:mt-20 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
              Certifications & Achievements
            </h2>
            <p className="mt-3 text-lg/8 text-gray-600">
              Continuous learning and professional development are key to delivering the best IT solutions.
            </p>
          </div>
          <ul
            role="list"
            className="mx-auto mt-10 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-16 
            text-center sm:grid-cols-3 md:grid-cols-4 lg:mx-0 lg:max-w-none lg:grid-cols-5 xl:grid-cols-6"
          >
            {certifications.map((cert) => (
              <li key={cert.name}>
                <img alt="" src={cert.imageUrl} className="mx-auto size-24 rounded-full" />
                <h3 className="mt-6 text-base/7 font-semibold tracking-tight text-gray-900">{cert.name}</h3>
                <p className="text-sm/6 text-gray-600">{cert.role}</p>
              </li>
            ))}
          </ul>
        </div>
        <br/>
        <br/>

        {/* Blog section (optional) */}
        {/* <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
              Latest Insights
            </h2>
            <p className="mt-2 text-lg/8 text-gray-600">Explore best practices, how-tos, and case studies.</p>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {blogPosts.map((post) => (
              <article
                key={post.id}
                className="relative isolate flex flex-col justify-end overflow-hidden rounded-2xl bg-gray-900 px-8 pb-8 pt-80 sm:pt-48 lg:pt-80"
              >
                <img alt="" src={post.imageUrl} className="absolute inset-0 -z-10 h-full w-full object-cover" />
                <div className="absolute inset-0 -z-10 bg-gradient-to-t from-gray-900 via-gray-900/40" />
                <div className="absolute inset-0 -z-10 rounded-2xl ring-1 ring-inset ring-gray-900/10" />

                <div className="flex flex-wrap items-center gap-y-1 overflow-hidden text-sm/6 text-gray-300">
                  <time dateTime={post.datetime} className="mr-8">
                    {post.date}
                  </time>
                  <div className="-ml-4 flex items-center gap-x-4">
                    <svg viewBox="0 0 2 2" className="-ml-0.5 h-0.5 w-0.5 flex-none fill-white/50">
                      <circle r={1} cx={1} cy={1} />
                    </svg>
                    <div className="flex gap-x-2.5">
                      <img alt="" src={post.author.imageUrl} className="h-6 w-6 flex-none rounded-full bg-white/10" />
                      {post.author.name}
                    </div>
                  </div>
                </div>
                <h3 className="mt-3 text-lg/6 font-semibold text-white">
                  <a href={post.href}>
                    <span className="absolute inset-0" />
                    {post.title}
                  </a>
                </h3>
              </article>
            ))}
          </div>
        </div> */}
      </main>

      {/* Footer */}
    </div>
  )
}
